import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';

import { Bold } from '../_ui/typography.component';
import { SecondaryButton } from '../_ui/buttons.component';
import { formatPhone } from '../../../lib/helpers/phone.helpers';
import {
  MobileMenuBar,
  MobileMenuTitle,
  MobileMenuBody,
  MobileMenuIdentity,
  MobileMenuIdentityLine,
  MobileMenuActionGroup,
} from './parent-header.styles';

export interface IParentMobileMenuProps {
  open: boolean;
  onClose: () => void;
  parent: IUser | null;
  // Optional back-nav target — when set, the menu surfaces a "Back" action.
  // Tapped action delegates to the same callback the desktop back button uses,
  // so URL-state preservation rules (?past=1 round-trips) stay in one place.
  onBack?: () => void;
  backLabel?: string;
  onAccount: () => void;
  onSignOut: () => void;
}

// Full-screen menu surfaced from the parent header's hamburger on phones.
// Mirrors the desktop chrome's affordances (back nav, account, sign out) plus
// the parent identity strip that the desktop header shows inline.
export const ParentMobileMenu: React.FC<IParentMobileMenuProps> = ({
  open, onClose, parent, onBack, backLabel = 'Back', onAccount, onSignOut,
}) => {
  const displayName = parent?.name || 'My account';
  const email = parent?.email;
  const phone = formatPhone(parent?.phone);

  // Run the action then close; the menu has no "stay open" affordance after
  // a navigation, and not closing leaves the dialog mounted over the new page.
  const runAndClose = (fn: () => void) => () => { fn(); onClose(); };

  return (
    <Dialog fullScreen open={open} onClose={onClose} aria-labelledby="parent-mobile-menu-title">
      <MobileMenuBar>
        <MobileMenuTitle id="parent-mobile-menu-title">{displayName}</MobileMenuTitle>
        <IconButton aria-label="Close menu" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </MobileMenuBar>

      <MobileMenuBody>
        <MobileMenuIdentity>
          <Bold>{displayName}</Bold>
          {email && <MobileMenuIdentityLine>{email}</MobileMenuIdentityLine>}
          {phone && <MobileMenuIdentityLine>{phone}</MobileMenuIdentityLine>}
        </MobileMenuIdentity>

        <MobileMenuActionGroup>
          {onBack && (
            <SecondaryButton
              fullWidth
              variant="outlined"
              startIcon={<ChevronLeftIcon />}
              onClick={runAndClose(onBack)}
            >
              {backLabel}
            </SecondaryButton>
          )}
          <SecondaryButton
            fullWidth
            variant="outlined"
            startIcon={<AccountCircleIcon />}
            onClick={runAndClose(onAccount)}
          >
            Account details
          </SecondaryButton>
          <SecondaryButton
            fullWidth
            variant="outlined"
            startIcon={<ExitToAppIcon />}
            onClick={runAndClose(onSignOut)}
          >
            Sign out
          </SecondaryButton>
        </MobileMenuActionGroup>
      </MobileMenuBody>
    </Dialog>
  );
};
